import { useEffect, useSyncExternalStore } from "react";
import { getNewsSummary, sendNewsReceipts } from "../../api/productNews";

// ─────────────────────────────────────────────────────────────────────────────
// Resumen de novedades del comercio, compartido por la campana del encabezado y
// el menú lateral (etiqueta "Nuevo"): un solo pedido por carga de página.
//
// Es un store de MÓDULO, igual que el cache de useSidebarBranding: el menú se
// monta dos veces a la vez (columna de escritorio + drawer móvil) y cruzar de
// ruta remonta el árbol. Se ata al usuario: si cambia la sesión sin recargar la
// página, se vuelve a pedir.
// ─────────────────────────────────────────────────────────────────────────────

const INITIAL = { userId: null, status: "idle", unread: 0, menuHighlights: [] };

let state = INITIAL;
let inflight = null;
const listeners = new Set();

function setState(patch) {
  state = { ...state, ...patch };
  listeners.forEach((listener) => listener());
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return state;
}

/** Pide el resumen para este usuario (una vez por usuario y carga de página). */
export function loadNewsSummary(userId) {
  if (!userId) return Promise.resolve(state);
  if (state.userId === userId && inflight) return inflight;
  setState({ ...INITIAL, userId, status: "loading" });
  inflight = getNewsSummary().then((summary) => {
    // Otra sesión empezó mientras tanto: esta respuesta ya no es de nadie.
    if (state.userId === userId) setState({ ...summary, status: "ready" });
    return state;
  });
  return inflight;
}

/** El panel trae el número exacto de no leídas al abrirse. */
export function setNewsUnread(unread) {
  setState({ unread: Math.max(0, unread) });
}

/** Normaliza para comparar con los `path` del menú (sin barra final). */
function normalizePath(pathname) {
  const value = String(pathname ?? "");
  return value.length > 1 ? value.replace(/\/+$/, "") : value;
}

/**
 * El usuario entró a una pantalla: si un ítem del menú la señalaba, la etiqueta
 * se apaga ya y el backend recibe `visited` por cada novedad que la señalaba.
 * Coincidencia EXACTA: el ítem del menú lleva a esa ruta.
 */
export function markMenuPathVisited(pathname) {
  const path = normalizePath(pathname);
  const highlight = state.menuHighlights.find((h) => h.path === path);
  if (!highlight) return;
  setState({ menuHighlights: state.menuHighlights.filter((h) => h !== highlight) });
  sendNewsReceipts(highlight.newsIds.map((id) => ({ id, event: "visited" })));
}

/** Solo para tests. */
export function resetNewsSummary() {
  state = INITIAL;
  inflight = null;
  listeners.forEach((listener) => listener());
}

/**
 * Resumen de novedades del usuario. Con `userId` null no pide nada (roles que
 * no son comercio) y devuelve el estado vacío.
 */
export function useNewsSummary(userId) {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot);
  useEffect(() => {
    if (userId) loadNewsSummary(userId);
  }, [userId]);
  return userId && snapshot.userId === userId ? snapshot : INITIAL;
}
